import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Star, Users, BookOpen, MessageCircle } from 'lucide-react';

const BenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: Heart,
      title: "Ensina crianças a lidar com emoções com leveza e fé",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: Star,
      title: "Desenvolve virtudes como generosidade, coragem e gratidão",
      color: "from-yellow-500 to-orange-500"
    },
    {
      icon: Users,
      title: "Cria laços diários entre pais e filhos",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: BookOpen,
      title: "Forma o hábito de oração desde cedo",
      color: "from-purple-500 to-indigo-500"
    },
    {
      icon: MessageCircle,
      title: "Facilita conversas importantes com uma linguagem acessível",
      color: "from-green-500 to-emerald-500"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <section className="py-20" style={{ backgroundColor: '#F7F5EF' }}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Mais do que atividades. Um solo fértil para fé e presença.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Com esse jardim, você planta todos os dias pequenas sementes que florescem em virtude, equilíbrio e conexão.
          </p>
        </motion.div>

        {/* Benefits List */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-4xl mx-auto space-y-6"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="flex items-center gap-6 p-6 bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20"
            >
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${benefit.color} flex items-center justify-center flex-shrink-0`}>
                <benefit.icon className="w-8 h-8 text-white" />
              </div>
              
              <p className="text-lg font-medium text-gray-800">
                {benefit.title}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Visual Elements */}
        <div className="relative mt-16">
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-4 bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20">
              <div className="text-4xl">🌱</div>
              <div className="text-2xl font-bold text-gray-900">
                Transforme sua rotina familiar
              </div>
              <div className="text-4xl">🌱</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
